import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Toast from '../components/Toast';
import '../styles/PRDList.css';
import '../styles/Pages.css';
import { supabase } from '../lib/supabase';
import { documentTemplateService, SectionConfig } from '../services/documentTemplateService';
import { documentService, Document } from '../services/documentService';
import { useActiveProject } from '../hooks/useActiveProject';
import { geminiService } from '../services/geminiService';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as CardViewIcon } from '../icons/taskboard_cardview.svg';
import { ReactComponent as ListViewIcon } from '../icons/taskboard_listview.svg';
import { DocListSkeleton } from '../components/SkeletonLoader';

const DOC_TYPES = [
  'Market Research',
  'User Persona',
  'Product Requirement',
  'User Stories',
  'Release Notes',
  'Feature Documentation'
];

// Section templates for each document type
const SECTION_TEMPLATES: Record<string, string[]> = {
  'Product Requirement': [
    'Overview',
    'Problem Statement',
    'Goals & Metrics',
    'User Personas',
    'User Stories',
    'Feature Requirements',
    'Edge Cases',
    'Acceptance Criteria',
    'Dependencies',
    'Open Questions'
  ],
  'Market Research': [
    'Executive Summary',
    'Market Overview',
    'Target Audience',
    'Competitive Analysis',
    'Market Trends',
    'SWOT Analysis',
    'Opportunities',
    'Recommendations',
    'Data Sources',
    'Appendix'
  ],
  'User Persona': [
    'Demographics',
    'Background',
    'Goals & Motivations',
    'Pain Points',
    'Behaviors',
    'Needs & Expectations',
    'Technology Usage',
    'Preferred Channels',
    'Quotes',
    'Scenarios'
  ],
  'User Stories': [
    'Epic Overview',
    'User Roles',
    'Story Map',
    'Acceptance Criteria',
    'Priority',
    'Effort Estimation',
    'Dependencies',
    'Notes',
    'Related Stories',
    'Testing Scenarios'
  ],
  'Release Notes': [
    'Release Overview',
    'New Features',
    'Improvements',
    'Bug Fixes',
    'Breaking Changes',
    'Deprecations',
    'Known Issues',
    'Migration Guide',
    'Credits',
    'Feedback'
  ],
  'Feature Documentation': [
    'Feature Overview',
    'Use Cases',
    'How It Works',
    'Setup & Configuration',
    'User Guide',
    'API Reference',
    'Examples',
    'Best Practices',
    'Troubleshooting',
    'FAQ'
  ]
};

// Content field configuration
interface ContentField {
  id: string;
  label: string;
  placeholder: string;
  description: string;
  icon: string;
  required?: boolean;
  type?: 'text' | 'textarea';
}

const CONTENT_FIELDS: Record<string, ContentField[]> = {
  'Product Requirement': [
    {
      id: 'problem',
      label: 'Problem Statement',
      placeholder: 'Describe the specific problem or pain point this product solves...',
      description: 'What is the core issue users are facing?',
      icon: '🎯',
      required: true,
      type: 'textarea'
    },
    {
      id: 'users',
      label: 'User Segments',
      placeholder: 'Who are we building this for? (e.g., Small Business Owners, Enterprise Admins)...',
      description: 'Define the target audience.',
      icon: '👥'
    },
    {
      id: 'goals',
      label: 'Goals & Success Metrics',
      placeholder: 'List the key objectives and how we will measure success (e.g., Increase conversion by 20%)...',
      description: 'What does success look like?',
      icon: '📈'
    },
    {
      id: 'requirements',
      label: 'High-Level Requirements',
      placeholder: 'Jot down the must-have features or capabilities...',
      description: 'Key functional requirements.',
      icon: '📝'
    }
  ],
  'Market Research': [
    {
      id: 'objectives',
      label: 'Research Objectives',
      placeholder: 'What do we want to learn from this research?',
      description: 'Main goals of the study.',
      icon: '🔍',
      required: true
    },
    {
      id: 'market',
      label: 'Target Market',
      placeholder: 'Describe the market size, demographics, and characteristics...',
      description: 'Who are we analyzing?',
      icon: '🌍'
    },
    {
      id: 'competitors',
      label: 'Key Competitors',
      placeholder: 'List main competitors and their strengths/weaknesses...',
      description: 'Who else is in this space?',
      icon: '⚔️'
    },
    {
      id: 'sources',
      label: 'Data Sources',
      placeholder: 'Where will the data come from? (e.g., Surveys, Interviews, Reports)...',
      description: 'Methodology and sources.',
      icon: '📊'
    }
  ],
  'User Persona': [
    {
      id: 'role',
      label: 'Persona Name & Role',
      placeholder: 'e.g., Sarah the Senior PM...',
      description: 'Give the persona a name and job title.',
      icon: '👤',
      required: true
    },
    {
      id: 'responsibilities',
      label: 'Key Responsibilities',
      placeholder: 'What does this person do on a daily basis?',
      description: 'Main tasks and duties.',
      icon: '💼'
    },
    {
      id: 'pain_points',
      label: 'Pain Points',
      placeholder: 'What frustrates them most?',
      description: 'Challenges they face.',
      icon: '😫'
    },
    {
      id: 'goals',
      label: 'Goals',
      placeholder: 'What are they trying to achieve?',
      description: 'Their primary motivations.',
      icon: '🏆'
    }
  ],
  'User Stories': [
    {
      id: 'role',
      label: 'User Role',
      placeholder: 'As a <role>...',
      description: 'The user type for this story.',
      icon: '🎭',
      required: true
    },
    {
      id: 'desire',
      label: 'Goal/Desire',
      placeholder: 'I want to <action>...',
      description: 'What the user wants to do.',
      icon: '✨'
    },
    {
      id: 'benefit',
      label: 'Benefit',
      placeholder: 'So that <benefit>...',
      description: 'The value they get.',
      icon: '🎁'
    },
    {
      id: 'criteria',
      label: 'Acceptance Criteria',
      placeholder: '- Given... When... Then...',
      description: 'Conditions for the story to be complete.',
      icon: '✅'
    }
  ],
  'Release Notes': [
    {
      id: 'version',
      label: 'Version & Date',
      placeholder: 'v1.0.0 - October 24, 2023',
      description: 'Release identifier.',
      icon: '🏷️',
      required: true
    },
    {
      id: 'features',
      label: 'Key Features',
      placeholder: '- Feature A: Description...',
      description: 'New capabilities added.',
      icon: '🚀'
    },
    {
      id: 'fixes',
      label: 'Bug Fixes',
      placeholder: '- Fixed issue with...',
      description: 'Issues resolved in this release.',
      icon: '🐛'
    },
    {
      id: 'issues',
      label: 'Known Issues',
      placeholder: 'List any remaining issues...',
      description: 'Things to be aware of.',
      icon: '⚠️'
    }
  ],
  'Feature Documentation': [
    {
      id: 'overview',
      label: 'Feature Overview',
      placeholder: 'Brief summary of what this feature does...',
      description: 'High-level explanation.',
      icon: 'ℹ️',
      required: true
    },
    {
      id: 'audience',
      label: 'Target Audience',
      placeholder: 'Who is this feature for?',
      description: 'Primary users.',
      icon: '🎯'
    },
    {
      id: 'functionality',
      label: 'Key Functionality',
      placeholder: 'List the main capabilities...',
      description: 'What can it do?',
      icon: '⚙️'
    },
    {
      id: 'prerequisites',
      label: 'Prerequisites',
      placeholder: 'Any required settings or permissions...',
      description: 'What is needed before using.',
      icon: '🔒'
    }
  ]
};

const PRDWriter: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showNewDocModal, setShowNewDocModal] = useState(false);
  const [modalStep, setModalStep] = useState(1); // 1: name & type, 2: sections, 3: content
  const [newDocName, setNewDocName] = useState('');
  const [newDocType, setNewDocType] = useState('Product Requirement');
  const [orderedSections, setOrderedSections] = useState<string[]>([]);
  const [enabledSections, setEnabledSections] = useState<Set<string>>(new Set());
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [contentValues, setContentValues] = useState<Record<string, string>>({});
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [docToDelete, setDocToDelete] = useState<string | null>(null);

  // AI Generation State
  const [useAI, setUseAI] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);

  const { activeProjectId } = useActiveProject();

  useEffect(() => {
    loadDocuments();
  }, [activeProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDocuments = async () => {
    try {
      const docs = await documentService.getDocuments(activeProjectId);
      setDocuments(docs);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleNewDocClick = () => {
    setShowNewDocModal(true);
    setModalStep(1);
  };

  const handleCancelModal = () => {
    setShowNewDocModal(false);
    setModalStep(1);
    setNewDocName('');
    setNewDocType('Product Requirement');
    setOrderedSections([]);
    setEnabledSections(new Set());
    setContentValues({});
  };

  const handleNextStep = async () => {
    if (modalStep === 1) {
      if (!newDocName.trim()) {
        alert('Please enter a document name');
        return;
      }

      setLoadingTemplate(true);

      try {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('User not authenticated');
        }

        // Try to load saved template
        const savedTemplate = await documentTemplateService.getTemplate(user.id, newDocType);

        if (savedTemplate && savedTemplate.length > 0) {
          // Use saved template
          const orderedSectionNames = savedTemplate
            .sort((a, b) => a.order - b.order)
            .map(s => s.name);
          const enabledSectionNames = new Set(
            savedTemplate.filter(s => s.enabled).map(s => s.name)
          );

          setOrderedSections(orderedSectionNames);
          setEnabledSections(enabledSectionNames);
        } else {
          // Use default template
          const sections = SECTION_TEMPLATES[newDocType] || [];
          setOrderedSections(sections);
          setEnabledSections(new Set(sections));
        }

        setModalStep(2);
      } catch (error) {
        console.error('Error loading template:', error);
        // Fallback to default template on error
        const sections = SECTION_TEMPLATES[newDocType] || [];
        setOrderedSections(sections);
        setEnabledSections(new Set(sections));
        setModalStep(2);
      } finally {
        setLoadingTemplate(false);
      }
    } else if (modalStep === 2) {
      setModalStep(3);
    }
  };

  const handleBackStep = () => {
    setModalStep(prev => prev - 1);
  };

  const handleResetToDefaults = () => {
    const defaultSections = SECTION_TEMPLATES[newDocType] || [];
    setOrderedSections(defaultSections);
    setEnabledSections(new Set(defaultSections));
  };

  const toggleSection = (section: string) => {
    setEnabledSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newSections = [...orderedSections];
    const draggedItem = newSections[draggedIndex];
    newSections.splice(draggedIndex, 1);
    newSections.splice(index, 0, draggedItem);

    setOrderedSections(newSections);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleContentChange = (fieldId: string, value: string) => {
    setContentValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleCreateDoc = async () => {
    console.log('=== handleCreateDoc called ===');
    console.log('useAI:', useAI);

    const selectedSections = orderedSections.filter(s => enabledSections.has(s));
    if (selectedSections.length === 0) {
      alert('Please select at least one section');
      return;
    }

    // Validate required fields
    const currentFields = CONTENT_FIELDS[newDocType] || [];
    const missingFields = currentFields.filter(field =>
      field.required && (!contentValues[field.id] || !contentValues[field.id].trim())
    );

    if (missingFields.length > 0) {
      alert(`Please fill in the required field: ${missingFields[0].label}`);
      return;
    }

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Save template customization
      const sectionConfigs: SectionConfig[] = orderedSections.map((name, index) => ({
        name,
        enabled: enabledSections.has(name),
        order: index
      }));

      await documentTemplateService.saveTemplate(user.id, newDocType, sectionConfigs);

      // Create initial HTML content
      let initialContent = '';
      let aiRequest = null;

      if (useAI) {
        console.log('AI generation is enabled, starting...');
        setIsGeneratingAI(true);
        try {
          // Prepare field labels for context
          const fieldLabels: Record<string, string> = {};
          CONTENT_FIELDS[newDocType]?.forEach(field => {
            fieldLabels[field.id] = field.label;
          });

          console.log('Calling geminiService.generateDocumentContent...');
          // Generate content here to show loading state
          const generatedContent = await geminiService.generateDocumentContent({
            docType: newDocType,
            sections: selectedSections,
            contentValues,
            fieldLabels
          });

          console.log('AI generation successful! Content length:', generatedContent.length);
          console.log('First 100 chars:', generatedContent.substring(0, 100));

          // We'll pass this content to the editor to "type out"
          aiRequest = {
            generatedContent
          };

          // Start with empty content in the actual doc so the editor can fill it
          initialContent = '';
        } catch (error) {
          console.error('AI Generation failed:', error);
          setToast({ message: 'AI generation failed. Falling back to template.', type: 'error' });
          // Fallback to standard generation
          selectedSections.forEach((section) => {
            const fieldId = CONTENT_FIELDS[newDocType]?.find(f => f.label === section)?.id;
            const value = fieldId ? contentValues[fieldId] : '';
            initialContent += `<h2>${section}</h2>`;
            if (value) initialContent += `<p>${value}</p>`;
            else initialContent += `<p><br></p>`;
            initialContent += `<p><br></p>`;
          });
        } finally {
          setIsGeneratingAI(false);
        }
      } else {
        console.log('AI generation is disabled, using standard template');
        // Standard generation
        selectedSections.forEach((section) => {
          const fieldId = CONTENT_FIELDS[newDocType]?.find(f => f.label === section)?.id;
          const value = fieldId ? contentValues[fieldId] : '';
          initialContent += `<h2>${section}</h2>`;
          if (value) initialContent += `<p>${value}</p>`;
          else initialContent += `<p><br></p>`;
          initialContent += `<p><br></p>`;
        });
      }

      console.log('Creating document in Supabase...');
      // Create document in Supabase
      const newDoc = await documentService.createDocument({
        title: newDocName,
        type: newDocType,
        content: initialContent,
        status: 'draft'
      });

      if (newDoc) {
        console.log('Document created successfully. ID:', newDoc.id);
        console.log('Navigating with aiRequest:', aiRequest);
        handleCancelModal();
        navigate(`/doc-editor/${newDoc.id}`, {
          state: {
            aiRequest,
            isNewDoc: true
          }
        });
      } else {
        throw new Error('Failed to create document');
      }
    } catch (error) {
      console.error('Error creating document:', error);
      setToast({ message: 'Failed to create document. Please try again.', type: 'error' });
    }
  };

  const handleDeleteDoc = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDocToDelete(id);
  };

  const confirmDelete = async () => {
    if (!docToDelete) return;

    await documentService.deleteDocument(docToDelete);
    setToast({ message: 'Document deleted successfully', type: 'success' });
    loadDocuments();
    setDocToDelete(null);
  };

  const cancelDelete = () => {
    setDocToDelete(null);
  };

  const handleEditDoc = (id: string) => {
    navigate(`/doc-editor/${id}`);
  };

  const currentContentFields = CONTENT_FIELDS[newDocType] || [];

  const filteredDocuments = documents.filter(doc =>
    doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <Layout>
        <div className="prd-list-container">
          {/* Header - Single Row */}
          <div className="prd-header-row-1">
            <div className="page-breadcrumb">
              <div className="page-breadcrumb-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                </svg>
              </div>
              <h1 className="page-breadcrumb-title">Docs Writer</h1>
            </div>
            <div className="header-right-section">
              <div className="view-toggle">
                <button
                  className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setViewMode('grid')}
                  title="Grid View"
                >
                  <CardViewIcon width="16" height="16" />
                  Grid
                </button>
                <button
                  className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                  title="List View"
                >
                  <ListViewIcon width="16" height="16" />
                  List
                </button>
              </div>
              <div className="search-bar">
                <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <input
                  type="text"
                  placeholder="Search docs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
              <button className="new-prd-button btn-primary-cta" onClick={handleNewDocClick}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                New Doc
              </button>
            </div>
          </div>

          {/* Document List or Empty State */}
          {loadingDocs ? (
            <div style={{ padding: '8px 0' }}><DocListSkeleton count={6} /></div>
          ) : filteredDocuments.length > 0 ? (
            viewMode === 'grid' ? (
              <div className="prd-list-grid">
                {filteredDocuments.map(doc => (
                  <div key={doc.id} className="prd-card" onClick={() => handleEditDoc(doc.id)}>
                    <div className="prd-card-header">
                      <div className="prd-icon">
                        {CONTENT_FIELDS[doc.type]?.[0]?.icon || '📄'}
                      </div>
                      <div className="prd-actions">
                        <button
                          className="icon-button delete-btn"
                          onClick={(e) => handleDeleteDoc(doc.id, e)}
                          title="Delete"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="prd-card-content">
                      <h3>{doc.title}</h3>
                      <span className="prd-type-badge">{doc.type}</span>
                      <p className="prd-date">Updated {new Date(doc.updated_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="list-view-container">
                {filteredDocuments.map(doc => (
                  <div key={doc.id} className="list-view-item" onClick={() => handleEditDoc(doc.id)}>
                    <div className="list-view-item-left">
                      <div className="list-view-icon">
                        {CONTENT_FIELDS[doc.type]?.[0]?.icon || '📄'}
                      </div>
                      <div className="list-view-info">
                        <h3 className="list-view-title">{doc.title}</h3>
                        <div className="list-view-meta">
                          <span className="list-view-type-badge">{doc.type}</span>
                          <div className="list-view-date">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                              <line x1="16" y1="2" x2="16" y2="6"></line>
                              <line x1="8" y1="2" x2="8" y2="6"></line>
                              <line x1="3" y1="10" x2="21" y2="10"></line>
                            </svg>
                            <span>Updated {new Date(doc.updated_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="list-view-actions">
                      <button
                        className="list-view-action-btn delete"
                        onClick={(e) => handleDeleteDoc(doc.id, e)}
                        title="Delete"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 6h18"></path>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📝</div>
              <h2>{searchQuery ? 'No Docs found' : 'No Docs yet'}</h2>
              <p>
                {searchQuery
                  ? 'Try a different search term'
                  : 'Create your first document to get started'}
              </p>
            </div>
          )}

          {/* New Doc Modal */}
          {showNewDocModal && (
            <div className="modal-overlay" onClick={handleCancelModal}>
              <div className="modal-popup modal-popup-large" onClick={(e) => e.stopPropagation()}>
                {modalStep === 1 && (
                  <>
                    <h2>Create New Document</h2>
                    <p>Enter document details to get started</p>

                    <div className="modal-form">
                      <div className="form-group">
                        <label htmlFor="doc-name">Document Name</label>
                        <input
                          id="doc-name"
                          type="text"
                          value={newDocName}
                          onChange={(e) => setNewDocName(e.target.value)}
                          placeholder="Enter document name"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleNextStep();
                            if (e.key === 'Escape') handleCancelModal();
                          }}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="doc-type">Document Type</label>
                        <select
                          id="doc-type"
                          value={newDocType}
                          onChange={(e) => setNewDocType(e.target.value)}
                        >
                          {DOC_TYPES.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="modal-actions">
                      <button className="modal-btn-cancel" onClick={handleCancelModal}>
                        Cancel
                      </button>
                      <button
                        className="modal-btn-primary"
                        onClick={handleNextStep}
                        disabled={!newDocName.trim()}
                      >
                        Next
                      </button>
                    </div>
                  </>
                )}

                {modalStep === 2 && (
                  <>
                    <div className="customize-header">
                      <div>
                        <h2>Customize Sections</h2>
                        <p>Drag to reorder • Click to enable/disable sections</p>
                      </div>
                      <button className="reset-button" onClick={handleResetToDefaults}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
                          <path d="M21 3v5h-5"></path>
                          <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
                          <path d="M3 21v-5h5"></path>
                        </svg>
                        Reset to Default
                      </button>
                    </div>

                    <div className="sections-list">
                      {orderedSections.map((section, index) => (
                        <div
                          key={section}
                          draggable
                          onDragStart={() => handleDragStart(index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragEnd={handleDragEnd}
                          className={`section-item ${enabledSections.has(section) ? 'enabled' : 'disabled'} ${draggedIndex === index ? 'dragging' : ''}`}
                        >
                          <svg className="drag-handle" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="9" y1="6" x2="9" y2="6.01"></line>
                            <line x1="15" y1="6" x2="15" y2="6.01"></line>
                            <line x1="9" y1="12" x2="9" y2="12.01"></line>
                            <line x1="15" y1="12" x2="15" y2="12.01"></line>
                            <line x1="9" y1="18" x2="9" y2="18.01"></line>
                            <line x1="15" y1="18" x2="15" y2="18.01"></line>
                          </svg>
                          <input
                            type="checkbox"
                            checked={enabledSections.has(section)}
                            onChange={() => toggleSection(section)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="section-name">{section}</span>
                        </div>
                      ))}
                    </div>

                    <div className="modal-actions">
                      <button className="modal-btn-cancel" onClick={handleBackStep}>
                        Back
                      </button>
                      <button
                        className="modal-btn-primary"
                        onClick={handleNextStep}
                        disabled={enabledSections.size === 0}
                      >
                        Next
                      </button>
                    </div>
                  </>
                )}

                {modalStep === 3 && (
                  <>
                    <h2>Initial Content</h2>
                    <p>Add some initial details for your {newDocType.toLowerCase()}</p>

                    <div className="modal-form content-form">
                      <div className="content-form-fields">
                        {CONTENT_FIELDS[newDocType]?.map(field => (
                          <div key={field.id} className="form-group">
                            <label>{field.label}</label>
                            <textarea
                              value={contentValues[field.id] || ''}
                              onChange={(e) => setContentValues({ ...contentValues, [field.id]: e.target.value })}
                              placeholder={field.placeholder}
                              rows={2}
                            />
                          </div>
                        ))}
                      </div>



                      <div className="ai-generation-option">
                        <label className="ai-checkbox-label">
                          <input
                            type="checkbox"
                            checked={useAI}
                            onChange={(e) => setUseAI(e.target.checked)}
                          />
                          <span className="ai-label-text">
                            <span className="ai-icon">✨</span>
                            Generate detailed content with Gemini AI
                          </span>
                        </label>
                        {useAI && <p className="ai-hint">AI will research and expand on your inputs to create a comprehensive document.</p>}
                      </div>
                    </div>

                    <div className="modal-actions">
                      <button
                        className="modal-btn-secondary"
                        onClick={() => setModalStep(2)}
                        disabled={isGeneratingAI}
                      >
                        Back
                      </button>
                      <button
                        className="modal-btn-primary"
                        onClick={handleCreateDoc}
                        disabled={isGeneratingAI}
                      >
                        {isGeneratingAI ? (
                          <span className="loading-text">
                            <span className="spinner-small"></span> Generating...
                          </span>
                        ) : 'Create Document'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {docToDelete && (
            <div className="modal-overlay" onClick={cancelDelete}>
              <div className="modal-popup" onClick={(e) => e.stopPropagation()}>
                <h2>Delete Document</h2>
                <p>Are you sure you want to delete this document? This action cannot be undone.</p>
                <div className="modal-actions">
                  <button className="modal-btn-danger" onClick={confirmDelete}>
                    Delete
                  </button>
                  <button className="modal-btn-secondary" onClick={cancelDelete}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Layout>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default PRDWriter;
